<template>
  <section v-if="displayList.length" class="ni-section ni-news" id="latest-news">
    <div class="ni-container">
      <SectionHeader
        variant="dark"
        :subtitle="t('newIndex.sections.news.subtitle')"
        more-href="/news"
      >
        <template #title>
          <span class="ni-section-header__title-mark">{{ t('newIndex.sections.news.title') }}</span>
        </template>
      </SectionHeader>

      <div class="ni-news__slider">
        <div class="ni-news__image-wrap">
          <div
            class="ni-news__image-stage ni-news__image-stage--clickable"
            role="button"
            :aria-label="t('newIndex.sections.news.viewDetail')"
            @click="goNewsDetail(activeNews.id)"
          >
            <Transition name="ni-news-fade" mode="out-in">
              <div
                :key="activeNews.id"
                class="ni-news__image-frame"
              >
                <img
                  :src="activeNews.imageUrl"
                  :alt="activeNews.title"
                  class="ni-news__image"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </Transition>
          </div>

          <div class="ni-news__panel">
            <h3 class="ni-news__panel-title">
              {{ t('newIndex.sections.news.glance') }}
            </h3>
            <div class="ni-news__list">
              <div
                v-for="(news, idx) in displayList"
                :id="`glance-news-item-${idx}`"
                :key="`news-${idx}-${news.id}`"
                class="ni-news__item"
                :class="{ 'ni-news__item--active': idx === currentIndex }"
                role="button"
                :aria-pressed="idx === currentIndex"
                @click.stop="handleSelectNews(idx)"
              >
                <h4 class="ni-news__item-title">{{ news.title }}</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SectionHeader from './shared/SectionHeader.vue';

const props = defineProps({
  newsList: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const router = useRouter();
const currentIndex = ref(0);
let autoPlayTimer = null;

const displayList = computed(() => props.newsList || []);

const activeNews = computed(() => {
  const list = displayList.value;
  if (!list.length) {
    return { id: '', title: '', imageUrl: '' };
  }
  return list[currentIndex.value] || list[0];
});

function stopTimer() {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer);
    autoPlayTimer = null;
  }
}

function startTimer() {
  stopTimer();
  const len = displayList.value.length;
  if (len <= 1) return;
  autoPlayTimer = setInterval(() => {
    currentIndex.value = (currentIndex.value + 1) % len;
  }, 6000);
}

function handleSelectNews(index) {
  currentIndex.value = index;
  startTimer();
}

function goNewsDetail(id) {
  if (!id) return;
  router.push({ name: 'news_detail', query: { id } });
}

watch(displayList, (list) => {
  if (currentIndex.value >= list.length) {
    currentIndex.value = 0;
  }
  startTimer();
});

onMounted(() => {
  startTimer();
});

onBeforeUnmount(() => {
  stopTimer();
});
</script>
